import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, lighten, Typography, useTheme } from "@mui/material";
import { type FC } from "react";
import { machineStatusSchema, type Machine } from "@packsize/schemas/machines";

import { getMachineDisplayStatus } from "@/views/dashboard/widgets/components/machine-utils";

interface MachineStatusProps {
	readonly machine: Machine;
	readonly onClick?: () => void;
}

export const MachineStatus: FC<MachineStatusProps> = ({ machine, onClick }) => {
	const theme = useTheme();
	const statusInfo = getMachineDisplayStatus(machine);

	const getIconConfig = () => {
		if (statusInfo.hasError) {
			return {
				icon: <ErrorOutlineIcon sx={{ fontSize: 24, color: theme.palette.error.main }} />,
				backgroundColor: theme.palette.error.light,
			};
		} else if (statusInfo.hasWarning) {
			return {
				icon: <WarningAmberIcon sx={{ fontSize: 24, color: theme.palette.warning.main }} />,
				backgroundColor: theme.palette.warning.light,
			};
		} else if (statusInfo.status === machineStatusSchema.enum.Online) {
			return {
				icon: <CheckCircleOutlineIcon sx={{ fontSize: 24, color: theme.palette.success.main }} />,
				backgroundColor: theme.palette.success.light,
			};
		} else if (statusInfo.status === machineStatusSchema.enum.Paused) {
			return {
				icon: <PauseCircleOutlineIcon sx={{ fontSize: 24, color: theme.palette.warning.main }} />,
				backgroundColor: theme.palette.warning.light,
			};
		} else if (statusInfo.status === machineStatusSchema.enum.Offline) {
			return {
				icon: <HighlightOffOutlinedIcon sx={{ fontSize: 24, color: theme.palette.common.black }} />,
				backgroundColor: theme.palette.grey[200],
			};
		} else if (statusInfo.status === machineStatusSchema.enum.Service) {
			return {
				icon: <WarningAmberIcon sx={{ fontSize: 24, color: theme.palette.warning.main }} />,
				backgroundColor: theme.palette.warning.light,
			};
		}
		return {
			icon: <HighlightOffOutlinedIcon sx={{ fontSize: 24, color: theme.palette.common.black }} />,
			backgroundColor: theme.palette.grey[200],
		};
	};

	const config = getIconConfig();

	return (
		<Box
			onClick={onClick}
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				gap: 1,
				padding: 1,
				borderRadius: 1,
				backgroundColor: lighten(config.backgroundColor, 0.7),
				cursor: onClick && (statusInfo.hasError || statusInfo.hasWarning) ? "pointer" : "default",
				"&:hover":
					onClick && statusInfo.status !== machineStatusSchema.enum.Online
						? {
								backgroundColor: lighten(config.backgroundColor, 0.6),
							}
						: {},
			}}
		>
			{config.icon}
			<Typography variant="caption" data-testid="machine-status__text__label">
				{statusInfo.status}
			</Typography>
		</Box>
	);
};
